import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { Filter, FilterState, FilterContextType } from './types';

const FilterContext = createContext<FilterContextType | undefined>(undefined);

interface FilterProviderProps {
  children: ReactNode;
  initialFilters?: Filter[];
  initialFilterState?: FilterState;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({
  children,
  initialFilters = [],
  initialFilterState = {},
}) => {
  const [filters] = useState<Filter[]>(initialFilters);
  const [filterState, setFilterState] =
    useState<FilterState>(initialFilterState);

  const updateFilter = useCallback(
    (filterId: string, value: Filter['value']) => {
      setFilterState((prev) => ({
        ...prev,
        [filterId]: value,
      }));
    },
    [],
  );

  const clearFilter = useCallback((filterId: string) => {
    setFilterState((prev) => {
      const newState = { ...prev };
      delete newState[filterId];
      return newState;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilterState({});
  }, []);

  const getFilterValue = useCallback(
    (filterId: string): Filter['value'] => {
      return filterState[filterId];
    },
    [filterState],
  );

  const isFilterActive = useCallback(
    (filterId: string): boolean => {
      const value = filterState[filterId];
      if (value === undefined || value === null) {
        return false;
      }

      // Check for empty arrays
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      // Check for empty date range
      if (
        typeof value === 'object' &&
        value !== null &&
        'start' in value &&
        'end' in value
      ) {
        return value.start !== null || value.end !== null;
      }

      return true;
    },
    [filterState],
  );

  const contextValue: FilterContextType = {
    filters,
    filterState,
    updateFilter,
    clearFilter,
    clearAllFilters,
    getFilterValue,
    isFilterActive,
  };

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    return {
      filters: [],
      filterState: {},
      clearAllFilters: () => {},
      updateFilter: () => {},
      clearFilter: () => {},
      getFilterValue: () => null,
      isFilterActive: () => false,
    };
  }
  return context;
};

// Convenience hooks for specific filter operations
export const useFilter = (filterId: string) => {
  const { getFilterValue, updateFilter, clearFilter, isFilterActive } =
    useFilterContext();

  return {
    value: getFilterValue(filterId),
    update: (value: Filter['value']) => updateFilter(filterId, value),
    clear: () => clearFilter(filterId),
    isActive: isFilterActive(filterId),
  };
};

export const useFilters = () => {
  const { filters, filterState, clearAllFilters } = useFilterContext();

  return {
    filters,
    filterState,
    clearAll: clearAllFilters,
  };
};
